import React from 'react';
import { fetchDataAnalystAgentSession, fetchDataAnalystAgentSessions } from '@services/dataAnalystAgentSessions';
import { logger } from '@utils/usefulFuncs';

export const useDataAnalystAgentSessions = () => {
  const [sessions, setSessions] = React.useState([]);
  const [isLoadingList, setIsLoadingList] = React.useState(true);
  const [listError, setListError] = React.useState(null);
  const [detailErrorById, setDetailErrorById] = React.useState({});

  const loadList = React.useCallback(async (cancel) => {
    setIsLoadingList(true);
    setListError(null);

    const { status, sessions: list, error } = await fetchDataAnalystAgentSessions();
    if (cancel && cancel.isCanceled) return;

    if (status === 200) {
      setSessions(list);
    } else {
      logger('[DAA sessions] list fetch failed', { status, error });
      setSessions([]);
      setListError(error || 'Failed to load previous sessions.');
    }
    setIsLoadingList(false);
  }, []);

  React.useEffect(() => {
    const cancel = { isCanceled: false };
    loadList(cancel);
    return () => {
      cancel.isCanceled = true;
    };
  }, [loadList]);

  const refetchList = React.useCallback(() => loadList(null), [loadList]);

  const fetchDetail = React.useCallback(async (id) => {
    setDetailErrorById((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

    const { status, session, error } = await fetchDataAnalystAgentSession(id);

    if (status === 200 && session) {
      return session;
    }

    const message =
      status === 404
        ? 'This session is no longer available.'
        : status === 429
        ? 'Daily limit reached. Please try again later.'
        : error || 'Failed to load session.';
    setDetailErrorById((prev) => ({ ...prev, [id]: message }));
    return null;
  }, []);

  return {
    sessions,
    isLoadingList,
    listError,
    refetchList,
    fetchDetail,
    detailErrorById,
  };
};
